// GAMBIARRA: usuários cuja autorização de produto expira em 2027 não devem
// receber a comunicação "Black2026" / Black Friday (banner, modal nem o fundo
// da gamificação). O backend não separa esses casos, então filtramos no
// cliente até a regra sair da campanha.
//
// Fonte única da regra — reutilizada em:
//  - useFetchPlatformBanners.ts (filtra banners/modals)
//  - Gamification/.../BlackFriday (escolhe o fundo escuro x o azul antigo)

export const BLACK_2026_TITLE = 'Black2026';

// `userProductsAuthorizationData` é um array (um item por produto). Usamos a
// data de expiração mais distante como critério — é até quando o usuário segue
// com acesso. O campo não está tipado em IUser (vem do backend), por isso o
// cast inline.
export const getFurthestExpirationYear = (user: unknown): number | null => {
  const authorizations = (
    user as {
      userProductsAuthorizationData?: Array<{ expirationDate?: string | null }>;
    } | null
  )?.userProductsAuthorizationData;

  if (!Array.isArray(authorizations)) return null;

  const times = authorizations
    .map((item) =>
      item?.expirationDate ? new Date(item.expirationDate).getTime() : NaN
    )
    .filter((time) => !Number.isNaN(time));

  if (times.length === 0) return null;

  return new Date(Math.max(...times)).getFullYear();
};

// true = ESCONDER a comunicação Black (expiração mais distante é exatamente 2027).
export const shouldHideBlack2026 = (user: unknown): boolean =>
  getFurthestExpirationYear(user) === 2027;

// true = PODE EXIBIR a comunicação Black para este usuário.
//
// Enquanto o usuário ainda não carregou (`user == null`, estado inicial do
// `useUserStore` antes da query `['me']` resolver), retornamos `false`. Sem
// isso, `shouldHideBlack2026(null)` é `false` e a comunicação aparece por um
// instante — "piscando" — para quem NÃO deveria vê-la (expira em 2027), sumindo
// só quando os dados do usuário chegam. Ou seja: só decidimos exibir quando há
// dados do usuário; na dúvida, não mostra.
export const shouldShowBlack2026 = (user: unknown): boolean =>
  user != null && !shouldHideBlack2026(user);

/* -------------------------------------------------------------------------- */
/* Tema padrão da campanha ("tema black")                                     */
/* -------------------------------------------------------------------------- */
//
// Durante a campanha Black o modo ESCURO é o padrão para todos os alunos
// (quem estava no claro vira dark, quem estava no dark continua, quem nunca
// escolheu fica dark). O aluno PODE trocar para o claro — e aí essa escolha
// é respeitada. Como `user.isDarkMode` (backend) é um booleano que não
// distingue "nunca escolheu" de "escolheu claro", gravamos a escolha explícita
// do aluno aqui no localStorage. Sem flag => padrão dark.
//
// Quando a campanha terminar (BLACK_FRIDAY_ENABLED = false), o app volta a usar
// `user.isDarkMode` normalmente e esta flag é ignorada.

const BLACK_THEME_CHOICE_KEY = 'black2026-theme-choice';

export const getBlack2026ThemeChoice = (): 'light' | 'dark' | null => {
  if (typeof window === 'undefined') return null;
  const value = window.localStorage.getItem(BLACK_THEME_CHOICE_KEY);
  return value === 'light' || value === 'dark' ? value : null;
};

export const setBlack2026ThemeChoice = (choice: 'light' | 'dark'): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BLACK_THEME_CHOICE_KEY, choice);
};

// Tema efetivo durante a campanha: a escolha explícita do aluno; na ausência
// dela, dark (padrão). true = dark.
export const isBlack2026DarkDefault = (): boolean =>
  getBlack2026ThemeChoice() !== 'light';
